export type Chemical = {
  isAlwaysReported?: boolean | null;
  isOverThreshold?: boolean | null;
  chemical?: { noHazardsNotReporting?: boolean | null } | null;
};

export function isChemicalReporting(
  chemical: Partial<Pick<Chemical, "chemical" | "isAlwaysReported" | "isOverThreshold">>
): boolean {
  // There is an intentional hierarchy here, the idea being
  // noHazardsNotReporting > the user has marked this explicitly not reported
  // isAlwaysReported > this product has been explicitly marked to always be reported
  // isOverThresholds > our system detects that this chemical is over threshold
  //    for the specific municipality (see recompute-values)
  // default true - this will catch any underspecified or malformed product,
  //    and send it through validation

  // This is new. This is being used in the underlying product catalog as a
  // way for the end-user to intentionally keep the product in Encamp, but
  // not report it as part of the Tier II reporting process
  if (chemical.chemical?.noHazardsNotReporting) {
    return false;
  }

  if (chemical.isAlwaysReported || chemical.isOverThreshold) {
    return true;
  }

  if (chemical.isOverThreshold === false) {
    return false;
  }

  // default to reporting if we have a malformed product, with the idea that
  // applying validation is a good thing
  return true;
}

/**
 * This is the where clause that is used to determine if a chemical is reporting.
 * This resembles the same logic as the isChemicalReporting function above.
 */
export const reportingFacilityChemicalReportingWhereClause = {
  AND: [
    {
      NOT: {
        chemical: {
          noHazardsNotReporting: true,
        },
      },
    },
    {
      OR: [{ isAlwaysReported: true }, { isOverThreshold: true }, { isOverThreshold: null }],
    },
  ],
};

/**
 * This is the where clause that is used to determine if a chemical is NOT reporting.
 * This is the logical inverse of isChemicalReportingWhereClause.
 * A chemical will NOT report if:
 * 1. It has noHazardsNotReporting set to true, OR
 * 2. It is not always reported AND is explicitly under threshold (false, not null)
 */
export const reportingFacilityChemicalNotReportingWhereClause = {
  OR: [
    // Chemical is explicitly marked as not reporting
    {
      chemical: {
        noHazardsNotReporting: true,
      },
    },
    // Chemical is not always reported AND is under threshold
    {
      AND: [
        {
          OR: [{ isAlwaysReported: false }, { isAlwaysReported: null }],
        },
        { isOverThreshold: false }, // Must be explicitly false, not null
      ],
    },
  ],
};

/**
 * Where clause for FacilityChemical table to determine if a chemical is reporting.
 * This is similar to isChemicalReportingWhereClause but adapted for the FacilityChemical
 * table structure where isOverThreshold is accessed through a relation.
 */
export const facilityChemicalReportingWhereClause = {
  AND: [
    {
      NOT: {
        chemical: {
          noHazardsNotReporting: true,
        },
      },
    },
    {
      OR: [
        { isAlwaysReported: true },
        {
          reportingFacilityChemical: {
            reportingFacilityChemical: {
              isOverThreshold: true,
            },
          },
        },
        {
          reportingFacilityChemical: {
            reportingFacilityChemical: {
              isOverThreshold: null,
            },
          },
        },
      ],
    },
  ],
};

/**
 * Where clause for FacilityChemical table to determine if a chemical is NOT reporting.
 * This is similar to isChemicalNotReportingWhereClause but adapted for the FacilityChemical
 * table structure where isOverThreshold is accessed through a relation.
 */
export const facilityChemicalNotReportingWhereClause = {
  OR: [
    // Chemical is explicitly marked as not reporting
    {
      chemical: {
        noHazardsNotReporting: true,
      },
    },
    // Chemical is not always reported AND is under threshold
    {
      AND: [
        {
          OR: [{ isAlwaysReported: false }, { isAlwaysReported: null }],
        },
        {
          reportingFacilityChemical: {
            reportingFacilityChemical: {
              isOverThreshold: false, // Must be explicitly false, not null
            },
          },
        },
      ],
    },
  ],
};
